import React, {useEffect} from 'react'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {AbnInput} from '../../../../common/component/AbnInput'
import {CheckboxInput} from '../../../../common/component/CheckboxInput'
import {CurrencyInput} from '../../../../common/component/CurrencyInput'
import {SelectInput} from '../../../../common/component/SelectInput'
import {TextInput} from '../../../../common/component/TextInput'
import {IApplicationFormData} from '../../../models/application-form'
import {employmentPath} from './employmentPath'
import {incomePath} from './incomePath'
import WizardSectionCard from './WizardSectionCard'

interface EmploymentIncomeSectionProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  trigger?: UseFormTrigger<IApplicationFormData>
}

const incomeFrequencyOptions = [
  {label: 'Annually', value: 'annually'},
  {label: 'Monthly', value: 'monthly'},
  {label: 'Fortnightly', value: 'fortnightly'},
  {label: 'Weekly', value: 'weekly'},
]

const governmentBenefitFrequency = {
  value: 'fortnightly',
  display: '/ fortnight',
}

const roleOptions = [
  {label: 'Sole proprietor', value: 'Sole proprietor'},
  {label: 'Director', value: 'Director'},
  {label: 'Partner', value: 'Partner'},
]

const businessStructureOptions = [
  {label: 'Sole trader', value: 'Sole trader'},
  {label: 'Pty Ltd', value: 'Pty Ltd'},
  {label: 'Partnership', value: 'Partnership'},
]

const isMissingRequiredValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true
  }

  if (typeof value === 'number') {
    return Number.isNaN(value)
  }

  return typeof value === 'string' && value.trim() === ''
}

const isFixedGovernmentBenefitFrequency = (value: unknown): boolean =>
  String(value ?? '')
    .trim()
    .toLowerCase() === governmentBenefitFrequency.value

const EmploymentIncomeSection: React.FC<EmploymentIncomeSectionProps> = ({
  register,
  control,
  errors,
  watch,
  setValue,
  trigger,
}) => {
  const isCentreLinkEnabled = Boolean(watch(incomePath('isCentreLink')))
  const hasPensionEnabled = Boolean(watch(incomePath('hasPension')))
  const hasNdisEnabled = Boolean(watch(incomePath('hasNdis')))
  const hasStudentSupportEnabled = Boolean(watch(incomePath('hasStudentSupport')))
  const hasEmploymentIncome = Boolean(watch(incomePath('hasEmploymentIncome')))
  const hasSelfEmploymentIncome = Boolean(watch(incomePath('hasSelfEmploymentIncome')))
  const pensionAmount = watch(incomePath('pensionAmount'))
  const pensionFrequency = watch(incomePath('pensionFrequency'))
  const centreAmount = watch(incomePath('centreAmount'))
  const centreLinkFrequency = watch(incomePath('centreLinkFrequency'))
  const studentSupportAmount = watch(incomePath('studentSupportAmount'))
  const studentSupportFrequency = watch(incomePath('studentSupportFrequency'))
  const uniqueStudentIdentifier = watch(employmentPath('uniqueStudentIdentifier'))
  const governmentBenefitsSelectedCount = [
    hasPensionEnabled,
    isCentreLinkEnabled,
    hasStudentSupportEnabled,
  ].filter(Boolean).length
  const hasMissingGovernmentBenefitFields =
    (hasPensionEnabled &&
      (isMissingRequiredValue(pensionAmount) || isMissingRequiredValue(pensionFrequency))) ||
    (isCentreLinkEnabled &&
      (isMissingRequiredValue(centreAmount) || isMissingRequiredValue(centreLinkFrequency))) ||
    (hasStudentSupportEnabled &&
      (isMissingRequiredValue(studentSupportAmount) ||
        isMissingRequiredValue(studentSupportFrequency) ||
        isMissingRequiredValue(uniqueStudentIdentifier)))

  useEffect(() => {
    if (hasPensionEnabled && !isFixedGovernmentBenefitFrequency(pensionFrequency)) {
      setValue(incomePath('pensionFrequency'), governmentBenefitFrequency.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }

    if (trigger) {
      trigger([incomePath('pensionAmount'), incomePath('pensionFrequency')])
    }
  }, [hasPensionEnabled, pensionFrequency, setValue, trigger])

  useEffect(() => {
    if (isCentreLinkEnabled && !isFixedGovernmentBenefitFrequency(centreLinkFrequency)) {
      setValue(incomePath('centreLinkFrequency'), governmentBenefitFrequency.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }

    if (trigger) {
      trigger([incomePath('centreAmount'), incomePath('centreLinkFrequency')])
    }
  }, [centreLinkFrequency, isCentreLinkEnabled, setValue, trigger])

  useEffect(() => {
    if (hasStudentSupportEnabled && !isFixedGovernmentBenefitFrequency(studentSupportFrequency)) {
      setValue(incomePath('studentSupportFrequency'), governmentBenefitFrequency.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })
    }

    if (trigger) {
      trigger([
        incomePath('studentSupportAmount'),
        incomePath('studentSupportFrequency'),
        employmentPath('uniqueStudentIdentifier'),
      ])
    }
  }, [hasStudentSupportEnabled, setValue, studentSupportFrequency, trigger])

  return (
    <>
      <WizardSectionCard
        title='Employment Income'
        subTitle='Salary or wages — full-time, part-time, casual'
        className='mb-5 p-8'
        variant='checkbox'
        defaultExpanded={hasEmploymentIncome}
        onExpandedChange={async (checked) => {
          setValue(incomePath('hasEmploymentIncome'), checked, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
          if (trigger) await trigger([incomePath('income'), incomePath('incomeFrequency')])
        }}
      >
        <div className='d-flex gap-4 '>
          <div style={{flex: 1}}>
            <CurrencyInput
              name={incomePath('income')}
              label='Amount (before tax)'
              placeholder='e.g. 75,000'
              control={control}
              errors={errors}
              prefix='$'
              helperText='Your gross salary before tax — the figure on your payslip or contract.'
            />
          </div>

          <div style={{flex: 1}}>
            <SelectInput
              name={incomePath('incomeFrequency')}
              label='Frequency'
              register={register}
              errors={errors}
              optionsList={incomeFrequencyOptions}
            />
          </div>
        </div>
      </WizardSectionCard>
      <WizardSectionCard
        title='Self-Employed Income'
        subTitle='ABN Income — business, freelance, contract'
        className='mb-5 p-8'
        variant='checkbox'
        defaultExpanded={hasSelfEmploymentIncome}
        onExpandedChange={async (checked) => {
          setValue(incomePath('hasSelfEmploymentIncome'), checked, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
          if (trigger)
            await trigger([
              incomePath('selfEmploymentIncome'),
              incomePath('selfEmploymentIncomeFrequency'),
              employmentPath('businessName'),
              employmentPath('businessStructure'),
              employmentPath('abn'),
              employmentPath('role'),
            ])
        }}
      >
        <div className='d-flex gap-4 '>
          <div style={{flex: 1}}>
            <CurrencyInput
              name={incomePath('selfEmploymentIncome')}
              label='Amount (net)'
              placeholder='Before tax'
              control={control}
              errors={errors}
              prefix='$'
              helperText="Your net business income — what's left after business expenses, before personal income tax."
            />
          </div>

          <div style={{flex: 1}}>
            <SelectInput
              name={incomePath('selfEmploymentIncomeFrequency')}
              label='Frequency'
              register={register}
              errors={errors}
              optionsList={incomeFrequencyOptions}
            />
          </div>
        </div>

        <hr className='mt-5 mb-8 text-muted'></hr>
        <h5 className='subheader2'>Business Details</h5>
        <small className='text-muted d-block fs-6'>
          The business or trust your incomme flows through.
        </small>

        <TextInput
          name={employmentPath('businessName')}
          label='Business / trading name'
          placeholder='e.g. Smith Carpentry'
          register={register}
          className='mt-5'
          errors={errors}
          required={true}
        />
        <AbnInput
          name={employmentPath('abn')}
          label='ABN'
          helperText='Your 11-digit Australian Business Number'
          placeholder='12 345 678 901'
          control={control}
          className='mt-5'
          errors={errors}
          required={true}
        />
        <SelectInput
          name={employmentPath('businessStructure')}
          label='Business structure'
          register={register}
          errors={errors}
          optionsList={businessStructureOptions}
          required={true}
        />
        <SelectInput
          name={employmentPath('role')}
          label='Your role'
          register={register}
          errors={errors}
          optionsList={roleOptions}
          required={true}
          layout='half'
        />
        <TextInput
          name={employmentPath('industryType')}
          label='Industry / trade type'
          placeholder=''
          register={register}
          className='mt-5'
          errors={errors}
        />
        <TextInput
          name={employmentPath('businessAddress')}
          label='Business address'
          placeholder=''
          register={register}
          className='mt-5'
          errors={errors}
        />
      </WizardSectionCard>
      <WizardSectionCard
        title='Government Benefits'
        subTitle='Pension / DSP, Centrelink, Student Support'
        className='p-10'
        variant='collapse'
        collapsedTitleAccessory={
          governmentBenefitsSelectedCount > 0 ? (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--ink-700)',
                background: 'var(--ink-100, #ECECEC)',
                padding: '2px 8px',
                borderRadius: '99px',
              }}
            >
              {governmentBenefitsSelectedCount} selected
            </span>
          ) : null
        }
        collapsedMessage={
          hasMissingGovernmentBenefitFields ? (
            <div className='text-danger fs-6 mt-2'>
              Complete the required details for selected benefits.
            </div>
          ) : null
        }
      >
        <CheckboxInput
          name={incomePath('hasPension')}
          label='Pension / DSP'
          register={register}
          errors={errors}
          className='mt-8'
          helperText='Age Pension, Disability Support Pension'
        />

        {hasPensionEnabled && (
          <div className='ms-10'>
            <div style={{maxWidth: '360px'}}>
              <label
                className='form-label fw-semibold'
                htmlFor={String(incomePath('pensionAmount'))}
              >
                Amount <span className='text-danger'>*</span>
              </label>
              <div className='d-flex align-items-start gap-3'>
                <div style={{maxWidth: '240px', flex: '1 1 0'}}>
                  <CurrencyInput
                    name={incomePath('pensionAmount')}
                    placeholder='500'
                    control={control}
                    errors={errors}
                    prefix='$'
                    required={true}
                    layout='half'
                    className='w-100 mw-100'
                  />
                </div>
                <span
                  className='text-muted fs-6'
                  style={{whiteSpace: 'nowrap', paddingTop: '0.75rem'}}
                >
                  {governmentBenefitFrequency.display}
                </span>
              </div>
            </div>
          </div>
        )}

        <CheckboxInput
          name={incomePath('isCentreLink')}
          label='Centrelink'
          register={register}
          className='mt-8'
          errors={errors}
          helperText='Any Centrelink payment — JobSeeker, Parenting Payment, FTB, Carer Payment, Youth Allowance, etc.'
        />

        {isCentreLinkEnabled && (
          <div className='ms-10'>
            <div style={{maxWidth: '360px'}}>
              <label
                className='form-label fw-semibold'
                htmlFor={String(incomePath('centreAmount'))}
              >
                Amount <span className='text-danger'>*</span>
              </label>
              <div className='d-flex align-items-start gap-3'>
                <div style={{maxWidth: '240px', flex: '1 1 0'}}>
                  <CurrencyInput
                    name={incomePath('centreAmount')}
                    placeholder='500'
                    control={control}
                    errors={errors}
                    prefix='$'
                    required={true}
                    layout='half'
                    className='w-100 mw-100'
                  />
                </div>
                <span
                  className='text-muted fs-6'
                  style={{whiteSpace: 'nowrap', paddingTop: '0.75rem'}}
                >
                  {governmentBenefitFrequency.display}
                </span>
              </div>
            </div>
          </div>
        )}

        {false && (
          <CheckboxInput
            name={incomePath('hasNdis')}
            label='NDIS'
            register={register}
            errors={errors}
            className='mt-8'
          />
        )}

        {hasNdisEnabled && (
          <div className='d-flex gap-4 ms-10'>
            <div style={{flex: 1}}>
              <CurrencyInput
                name={incomePath('ndisAmount')}
                label='Amount'
                placeholder='500'
                control={control}
                errors={errors}
                prefix='$'
                required={true}
                layout='half'
              />
            </div>
            <div style={{flex: 1}}>
              <SelectInput
                name={incomePath('ndisFrequency')}
                label='Frequency'
                register={register}
                errors={errors}
                optionsList={incomeFrequencyOptions}
                required={true}
                layout='half'
              />
            </div>
          </div>
        )}

        <CheckboxInput
          name={incomePath('hasStudentSupport')}
          label='Student Support'
          register={register}
          errors={errors}
          className='mt-8'
          specialText='Student · USI needed'
          helperText='Youth Allowance, Austudy, ABSTUDY'
        />

        {hasStudentSupportEnabled && (
          <>
            <div className='ms-10'>
              <div style={{maxWidth: '360px'}}>
                <label
                  className='form-label fw-semibold'
                  htmlFor={String(incomePath('studentSupportAmount'))}
                >
                  Amount <span className='text-danger'>*</span>
                </label>
                <div className='d-flex align-items-start gap-3'>
                  <div style={{maxWidth: '240px', flex: '1 1 0'}}>
                    <CurrencyInput
                      name={incomePath('studentSupportAmount')}
                      placeholder='500'
                      control={control}
                      errors={errors}
                      prefix='$'
                      required={true}
                      layout='half'
                      className='w-100 mw-100'
                    />
                  </div>
                  <span
                    className='text-muted fs-6'
                    style={{whiteSpace: 'nowrap', paddingTop: '0.75rem'}}
                  >
                    {governmentBenefitFrequency.display}
                  </span>
                </div>
              </div>
            </div>
            <div className='d-flex gap-4 ms-10 mt-3'>
              <TextInput
                name={employmentPath('uniqueStudentIdentifier')}
                label='USI (Unique Student Identifier)'
                placeholder='e.g. ABC1234567'
                register={register}
                errors={errors}
                required={true}
                layout='half'
                maxLength={10}
                uppercase
                helperText='Your 10-character USI from usi.gov.au — verifies your student status.'
              />
            </div>
          </>
        )}
      </WizardSectionCard>
    </>
  )
}

export default EmploymentIncomeSection
